/** Shared types for the publish-progress system. Both ``/publish`` and
 *  ``/market/publish`` push jobs into the global context; the floating
 *  panel in Layout reads them. Keeping the types here (instead of inline
 *  in the context module) lets pages import the shape without pulling
 *  in the EventSource machinery. */

/** Overall job state — coarse-grained roll-up shown as the leading dot
 *  on each panel row. ``cancelled`` is a user-initiated terminal that's
 *  not an error: surfaced separately so the panel doesn't shout red for
 *  a deliberate stop. */
export type JobState = "running" | "done" | "error" | "cancelled";

/** Per-platform half. Each job publishes to MyHome and (optionally)
 *  SS.ge in parallel; the panel surfaces both halves so the user can
 *  tell when only one portal succeeded — the case that motivated the
 *  rebuild after a bulk run wrote a placement only on the MH side. */
export type PlatformStatus =
  | "pending"
  | "running"
  | "done"
  | "error"
  | "skipped";

/** Per-job record stored in the context's jobs map. ``jobId`` keys the
 *  map and matches the in-memory id from ``/api/stream/{jobId}``;
 *  ``jobRunId`` is the durable DB row used by the polling fallback when
 *  the stream drops (deploy, network blip, server restart). */
export interface PublishJobView {
  jobId: string;
  jobRunId: string;
  /** Where the job was launched from. Drives the row's title prefix
   *  ("Bulk · Saburtalo studio" vs "Single · MyHome.ge link"). */
  origin: "single" | "bulk";
  /** Friendly title for the row. Bulk passes the matched offer's
   *  scraped title; single publish passes the source URL. */
  title: string | null;
  /** The original portal URL the user pasted / claimed. Used as the
   *  row's secondary line when ``title`` is empty. */
  sourceUrl: string;
  /** Display name of the agent the job will publish under. ``null``
   *  means "the viewer themselves" (single publish) — no need to
   *  surface their own name to themselves. */
  agentLabel: string | null;
  /** Avatar URL of the agent the job runs under — drives the panel's
   *  "who ran this" affordance. ``null`` → the Avatar falls back to
   *  initials, or (for the viewer's own jobs) the panel substitutes the
   *  signed-in user's avatar. */
  agentAvatarUrl: string | null;
  /** Who *launched* the job (clicked Publish / Retry), as opposed to the
   *  agent it publishes *as*. Differs from ``agentLabel`` for a
   *  manager-driven bulk publish or a publish-on-behalf; equal to it for
   *  a self-publish (the panel then shows just one identity). ``null``
   *  for legacy jobs that predate the backend recording it. */
  triggeredByName: string | null;
  triggeredByAvatarUrl: string | null;
  /** Whether SS publishing was requested. Drives whether the panel
   *  reserves a chip for SS at all. */
  useSs: boolean;
  state: JobState;
  /** Latest pipeline event message — short, human-readable. */
  lastMessage: string;
  /** ms since epoch when the job began. The panel ticks an elapsed
   *  timer off this so the user can spot stalled jobs. */
  startedAt: number;
  /** Per-platform fine-grained status. Flipped to ``done`` /
   *  ``error`` on the terminal pipeline event using
   *  ``resolvePlatformsFromResult``. */
  mhStatus: PlatformStatus;
  ssStatus: PlatformStatus;
  /** Final published URLs. Filled in on the ``finished`` event so the
   *  user can click straight from the panel without navigating to
   *  /placements. */
  mhUrl: string | null;
  ssUrl: string | null;
  /** Per-platform progress percentage 0-100. Driven by the SSE step
   *  stream — see ``MH_STEP_SEQUENCE`` / ``SS_STEP_SEQUENCE`` in
   *  ``Context.tsx``. ``null`` until the first event lands. */
  mhPercent: number | null;
  ssPercent: number | null;
  /** Internal cursor into the step sequence for monotonic advance.
   *  Not rendered — the panel only reads the percent. */
  mhStepIdx: number;
  ssStepIdx: number;
  /** Per-platform failure reason, parsed from the pipeline result on the
   *  terminal event. Drives the inline error text + the per-platform
   *  retry control on a failed chip. ``null`` when that platform didn't
   *  fail (or hasn't finished). */
  mhError: string | null;
  ssError: string | null;
  /** Set on jobs spawned by the per-platform retry: ``"ss"`` means this
   *  run only re-publishes SS.ge (MyHome is left alone), ``"myhome"`` the
   *  reverse. ``undefined`` for normal both-platform jobs. The panel uses
   *  it to render the untouched platform as ``skipped``. */
  onlyPlatform?: "myhome" | "ss";
  /** Live control state surfaced via the ``GET /api/job-runs/{id}``
   *  polling response. Used to render the pause / resume / stop buttons
   *  and to dim the row while a stop is mid-flight. */
  paused?: boolean;
  stopRequested?: boolean;
  /** True for jobs discovered via the agency-wide poller (teammates'
   *  work). False / undefined for jobs the viewer spawned themselves.
   *  The floating panel splits its two cards on this flag — own
   *  publishes in one card, teammates' in another. */
  isForeign?: boolean;
}

/** Minimal payload pages send when they spawn a job. The context fills
 *  in defaults (``startedAt``, ``mhStatus`` etc.) and starts watching. */
export interface PublishJobRegisterIn {
  jobId: string;
  jobRunId: string;
  origin: "single" | "bulk";
  title: string | null;
  sourceUrl: string;
  agentLabel: string | null;
  /** Avatar URL of the publishing agent. Optional — pages pass it when
   *  known; the agency poller fills it from the bulk-jobs API. */
  agentAvatarUrl?: string | null;
  /** Who launched the job. Optional — page-spawned jobs leave it unset
   *  (the panel falls back to the signed-in viewer); the agency poller
   *  and per-platform retry pass it explicitly. */
  triggeredByName?: string | null;
  triggeredByAvatarUrl?: string | null;
  useSs: boolean;
  /** Real job start time (ms since epoch). Pages omit it (the job is
   *  starting now); the agency poller passes the persisted ``started_at``
   *  so a teammate's row shows when their run actually began, not when
   *  this tab first noticed it. */
  startedAt?: number;
  /** Set by the agency-wide poller when registering a teammate's job.
   *  Page-driven registrations (single publish, bulk publish) omit it. */
  isForeign?: boolean;
  /** Set when registering a per-platform retry job so the panel shows
   *  the untouched platform as ``skipped`` instead of a stuck spinner. */
  onlyPlatform?: "myhome" | "ss";
}

/** Terminal "the source listing is gone" case. The backend reports it as
 *  a normal ``error`` JobState, but it isn't a failure the broker can act
 *  on — the source listing was deleted/deactivated on the portal and
 *  can't be republished. The panel renders these in their own muted sky
 *  tone (not alarming red), drops the Retry affordance, and counts them
 *  as "removed" rather than "failed". Detection keys off the backend's
 *  fixed message (the ``ListingUnavailableError`` handler in
 *  ``app/routes/pipeline.py``) — keep this in sync if that wording
 *  changes. */
/** Aggregate view of one bulk-publish batch — replaces per-job tracking
 *  for bulk flows (market bulk-publish, manual bulk-publish, bulk-rerun).
 *  Backed by ``GET /api/job-runs/batch/{batchId}``, one query regardless
 *  of how many listings are in the batch. ``running`` folds in the
 *  backend's ``pending`` bucket (queued behind the pipeline concurrency
 *  cap) — the panel only shows running / done / failed, not the internal
 *  pending/error/cancelled split. */
export interface PublishBatchView {
  batchId: string;
  useSs: boolean;
  createdAt: number;
  total: number;
  running: number;
  done: number;
  failed: number;
  /** Count of still-live jobs with a pause in effect. 0 when none / when
   *  every worker has already exited. */
  paused: number;
  /** True once ``running`` hits 0 — the panel keeps showing the final
   *  tally until the user dismisses it, it just stops polling. */
  resolved: boolean;
}

/** Payload a page sends right after a bulk-publish endpoint returns —
 *  one call registers the whole batch instead of looping ``register()``
 *  per job. */
export interface PublishBatchRegisterIn {
  batchId: string;
  total: number;
  useSs: boolean;
}

export function isListingUnavailable(job: PublishJobView): boolean {
  if (job.state !== "error") return false;
  const m = (job.lastMessage || "").toLowerCase();
  return (
    m.includes("deleted or deactivated") ||
    m.includes("no longer be republished")
  );
}
